import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PhotoAnalyzer = ({ onAnalysisComplete }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max size is 5MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAnalyze = async () => {
    if (!preview) {
      toast.error('Please select an image first');
      return;
    }

    setAnalyzing(true);

    try {
      const response = await axios.post(`${API}/items/analyze-photo`, {
        image_base64: preview
      });

      if (response.data.items && response.data.items.length > 0) {
        onAnalysisComplete(response.data.items);
        toast.success(`Found ${response.data.items.length} items!`);
      } else {
        toast.info('No items detected. Try a clearer photo.');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast.error('Failed to analyze photo. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
        {preview ? (
          <div className="space-y-4">
            <img
              src={preview}
              alt="Preview"
              data-testid="photo-preview"
              className="max-h-64 mx-auto rounded-lg"
            />
            <Button
              onClick={() => setPreview(null)}
              variant="outline"
              data-testid="clear-photo-button"
              className="text-sm"
            >
              Choose Different Photo
            </Button>
          </div>
        ) : (
          <label htmlFor="photo-upload" className="cursor-pointer">
            <input
              id="photo-upload"
              type="file"
              accept="image/*"
              data-testid="photo-upload-input"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="space-y-2">
              <Upload className="w-12 h-12 mx-auto text-gray-400" />
              <p className="text-gray-600">Click to upload a photo</p>
              <p className="text-sm text-gray-400">PNG, JPG up to 5MB</p>
            </div>
          </label>
        )}
      </div>

      {preview && (
        <Button
          onClick={handleAnalyze}
          disabled={analyzing}
          data-testid="analyze-button"
          className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white"
        >
          {analyzing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            'Analyze Photo'
          )}
        </Button>
      )}

      <p className="text-sm text-gray-500 text-center">
        AI will identify items in your photo and add them to your inventory
      </p>
    </div>
  );
};

export default PhotoAnalyzer;
